/**
 * useViewport Hook
 *
 * Provides viewport dimensions and breakpoint information for responsive design.
 */

import { useState, useEffect } from 'react';

const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  wide: 1280,
} as const;

type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'wide';

interface ViewportState {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  isMobile: boolean; // < 768px
  isTouch: boolean;
  isPortrait: boolean; // width < height
  isLandscape: boolean; // width >= height
}

function getViewportState(): ViewportState {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const height = typeof window !== 'undefined' ? window.innerHeight : 768;

  let breakpoint: Breakpoint = 'wide';
  if (width < BREAKPOINTS.mobile) breakpoint = 'mobile';
  else if (width < BREAKPOINTS.tablet) breakpoint = 'tablet';
  else if (width < BREAKPOINTS.desktop) breakpoint = 'desktop';

  return {
    width,
    height,
    breakpoint,
    isMobile: width < BREAKPOINTS.tablet,
    isTouch: typeof window !== 'undefined' && 'ontouchstart' in window,
    isPortrait: width < height,
    isLandscape: width >= height,
  };
}

export function useViewport(): ViewportState {
  const [state, setState] = useState<ViewportState>(() => getViewportState());

  useEffect(() => {
    const handleResize = () => setState(getViewportState());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return state;
}

export { BREAKPOINTS };
export type { Breakpoint, ViewportState };
